import { Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { MessageBar, MessageBarBody, Spinner } from '@fluentui/react-components';
import AppLayout from './components/layout/AppLayout';
import { RouteErrorBoundary } from './components/common/RouteErrorBoundary';
import { useDbWarmup } from './hooks/useDbWarmup';
import { useBackgroundStream } from './hooks/useBackgroundStream';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { coldRouteLoaders } from './routes/coldRoutes';
import DashboardPage from './pages/DashboardPage';
import ClientsPage from './pages/ClientsPage';
import ClientDetailPage from './pages/ClientDetailPage';
import ReviewsPage from './pages/ReviewsPage';
import ReviewDetailPage from './pages/ReviewDetailPage';
import LoginPage from './pages/LoginPage';
import { SupervisionSignalRProvider } from './hooks/useSupervisionSignalR';
import MomentHost from './components/moments/MomentHost';
import MomentsWatcher from './components/moments/MomentsWatcher';
import i18n, { useTranslation } from './i18n';
import { UiLanguageBootstrap } from './i18n/useUiLanguage';

// Cold routes lazy-load (and background-stream — see useBackgroundStream). Named
// exports are re-shaped to default for React.lazy.
const [meetings, activeMeeting, actionItems, workload, productivity, taskLog, bookkeeping, settings, staffSettings, emailTemplates, patchNotes, quickStart, notifications] = coldRouteLoaders;
const MeetingsPage = lazyWithRetry(() =>
  meetings.load().then((m) => ({ default: (m as typeof import('./pages/MeetingsPage')).MeetingsPage })),
);
const ActiveMeetingPage = lazyWithRetry(() =>
  activeMeeting.load().then((m) => ({ default: (m as typeof import('./pages/ActiveMeetingPage')).ActiveMeetingPage })),
);
const ActionItemsPage = lazyWithRetry(() =>
  actionItems.load().then((m) => ({ default: (m as typeof import('./pages/ActionItemsPage')).ActionItemsPage })),
);
const WorkloadPage = lazyWithRetry(() =>
  workload.load().then((m) => ({ default: (m as typeof import('./pages/WorkloadPage')).WorkloadPage })),
);
const ProductivityPage = lazyWithRetry(() =>
  productivity.load().then((m) => ({ default: (m as typeof import('./pages/ProductivityPage')).ProductivityPage })),
);
const TaskLogPage = lazyWithRetry(() =>
  taskLog.load().then((m) => ({ default: (m as typeof import('./pages/TaskLogPage')).TaskLogPage })),
);
const BookkeepingPage = lazyWithRetry(() =>
  bookkeeping.load().then((m) => ({ default: (m as typeof import('./pages/BookkeepingPage')).default })),
);
const SettingsPage = lazyWithRetry(() =>
  settings.load().then((m) => ({ default: (m as typeof import('./pages/SettingsPage')).default })),
);
const StaffSettingsPage = lazyWithRetry(() =>
  staffSettings.load().then((m) => ({ default: (m as typeof import('./pages/StaffSettingsPage')).default })),
);
const EmailTemplatesPage = lazyWithRetry(() =>
  emailTemplates.load().then((m) => ({ default: (m as typeof import('./pages/EmailTemplatesPage')).default })),
);
const PatchNotesPage = lazyWithRetry(() =>
  patchNotes.load().then((m) => ({ default: (m as typeof import('./pages/PatchNotesPage')).default })),
);
const QuickStartPage = lazyWithRetry(() =>
  quickStart.load().then((m) => ({ default: (m as typeof import('./pages/QuickStartPage')).default })),
);
// The hub's notification list (fleet staff notifications, spec v0.2 §8): the bell's "See all".
const NotificationsPage = lazyWithRetry(() =>
  notifications.load().then((m) => ({ default: (m as typeof import('./pages/NotificationsPage')).NotificationsPage })),
);

/**
 * Fires the JIT database warm-up as soon as the authenticated app mounts and shows an
 * honest notice while the serverless SQL db rides out its ~40s auto-pause resume.
 * Rendered inside the authenticated tree so the warm-up call carries a bearer token.
 */
function DbWarmupNotice() {
  const { waking } = useDbWarmup();
  const { t } = useTranslation();
  if (!waking) return null;
  return (
    <MessageBar intent="info">
      <MessageBarBody>{t('shell.databaseWaking')}</MessageBarBody>
    </MessageBar>
  );
}

/** Kicks the idle background stream once the authenticated app is up. Renders nothing. */
function BackgroundStreamer() {
  useBackgroundStream();
  return null;
}

function App() {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();

  if (inProgress !== InteractionStatus.None) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <Spinner size="large" label={i18n.t('shell.signingIn')} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <SupervisionSignalRProvider>
      {/* The signed-in person's UI language: their own choice, else the CRM's record, else the browser. */}
      <UiLanguageBootstrap />
      {/* Accomplishment moments: one host for the signed-in app, and the watcher that fires them. */}
      <MomentHost>
      <MomentsWatcher />
      <AppLayout>
        <DbWarmupNotice />
        <BackgroundStreamer />
        <RouteErrorBoundary>
          <Suspense
            fallback={
              <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
                <Spinner label={i18n.t('shell.loadingPage')} />
              </div>
            }
          >
            <Routes>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/clients" element={<ClientsPage />} />
              <Route path="/clients/:id" element={<ClientDetailPage />} />
              <Route path="/reviews" element={<ReviewsPage />} />
              <Route path="/reviews/:id" element={<ReviewDetailPage />} />
              <Route path="/meetings" element={<MeetingsPage />} />
              <Route path="/meetings/:id" element={<ActiveMeetingPage />} />
              <Route path="/action-items" element={<ActionItemsPage />} />
              <Route path="/workload" element={<WorkloadPage />} />
              <Route path="/productivity" element={<ProductivityPage />} />
              <Route path="/task-log" element={<TaskLogPage />} />
              <Route path="/bookkeeping" element={<BookkeepingPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/staff-settings" element={<StaffSettingsPage />} />
              <Route path="/settings/email-templates" element={<EmailTemplatesPage />} />
              <Route path="/patch-notes" element={<PatchNotesPage />} />
              <Route path="/quick-start" element={<QuickStartPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
            </Routes>
          </Suspense>
        </RouteErrorBoundary>
      </AppLayout>
      </MomentHost>
    </SupervisionSignalRProvider>
  );
}

export default App;
